import React, { useMemo, useState } from "react";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import { Dispatch } from "redux";

import useGetFartherAgreement from "@fartherfinance/frontend/api/Document/hooks/useGetFartherAgreement";
import useGetInvestmentAdvisoryAgreement from "@fartherfinance/frontend/api/Document/hooks/useGetInvestmentAdvisoryAgreement";
import useGetPrivacyPolicy from "@fartherfinance/frontend/api/Document/hooks/useGetPrivacyPolicy";
import usePutUpdateAgreement from "@fartherfinance/frontend/api/Document/hooks/usePutUpdateAgreement";
import getSignatureUrl from "@fartherfinance/frontend/api/Document/requests/getSignatureUrl";
import { HellosignAgreement } from "@fartherfinance/frontend/api/Document/requests/types";
import {
  ClientChecklist,
  ClientChecklistStatus,
} from "@fartherfinance/frontend/api/Entity/requests/getClientChecklistV2";

import { DashboardForm } from "../DashboardForms/Types";
import { DocumentsCenterPath } from "@src/config/routing/RouterPaths";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import HelloSignProvider, {
  useHelloSign,
} from "@src/multiCustodian/providers/HelloSignProvider/HelloSignProvider";
import { MiscReducerActions } from "@src/multiCustodian/reducers/misc_Reducer";
import useThemeFragment from "@src/multiCustodian/theme/useThemeFragment";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";

import Checklist from "./components/Checklist/Checklist";
import DisclosuresModal from "./components/DisclosuresModal/DisclosuresModal";
import FartherAgreementsModal from "./components/FartherAgreementsModal/FartherAgreementsModal";
import {
  ClientChecklistDisplayValue,
  ClientChecklistKey,
  clientChecklistKeyToDisplayValueMap,
} from "./utils";

import type { ChecklistEntryDetails } from "./components/ChecklistEntry/ChecklistEntry";

type ChecklistItem = {
  name: ClientChecklistDisplayValue;
  value: ClientChecklistStatus;
};

interface ChecklistControllerInternalProps {
  checklist: ClientChecklist;
}

const ChecklistControllerInternal: React.FC<
  ChecklistControllerInternalProps
> = ({ checklist }) => {
  const [isPreparingHelloSign, setIsPreparingHelloSign] = useState(false);
  const [isFartherAgreementsModalOpen, setIsFartherAgreementsModalOpen] =
    useState(false);
  const [isDisclosuresModalOpen, setIsDisclosuresModalOpen] = useState(false);
  const dispatch = useDispatch<Dispatch<MiscReducerActions>>();
  const navigate = useNavigate();
  const t = useThemeFragment();

  const statusNotification = useStatusNotification();
  const { isImpersonationMode } = useApplicationMode();
  const helloSignClient = useHelloSign();
  const auth = useRequestAuth();
  const authOrNull = isImpersonationMode ? null : auth;
  const clientId = auth?.clientId ?? null;

  const callPutUpdateAgreement = usePutUpdateAgreement(clientId, auth);

  const isOldChecklist = checklist.version === "2000-01-01";
  const fartherAgreement = useGetFartherAgreement(
    isOldChecklist ? authOrNull : null
  );
  const investmentAdvisoryAgreement = useGetInvestmentAdvisoryAgreement(
    isOldChecklist ? null : authOrNull
  );
  const privacyPolicy = useGetPrivacyPolicy(isOldChecklist ? authOrNull : null);

  const checklistItems = useMemo(() => {
    return Object.entries(checklist)
      .filter(([name]) => name !== "version")
      .filter(([name]) => name !== "masterDisplay")
      .filter(([name]) => name !== "onboardingComplete")
      .map(([name, value]) => ({
        name: clientChecklistKeyToDisplayValueMap[name as ClientChecklistKey],
        value,
      })) as ChecklistItem[];
  }, [checklist]);

  const isEveryStepCompletedBeforeCustodianAgreement = checklistItems
    .filter((item) => item.name !== "Custodian Agreement")
    .every((item) => item.value === ClientChecklistStatus.enum.Completed);

  const handleSetDrawerPanelContext = (formToShow: DashboardForm): void => {
    dispatch({
      type: "SET_DRAWER_PANEL_CONTEXT",
      payload: { formToShow },
    });
  };

  const handleNavigateToDocumentsCenter = (): void => {
    if (clientId) {
      navigate(`/Client/${clientId}/${DocumentsCenterPath}`);
    }
  };

  const handleSignAgreementViaHelloSign = async (
    agreement: HellosignAgreement
  ): Promise<void> => {
    if (
      auth === null ||
      helloSignClient === undefined ||
      agreement.status !== "Pending" ||
      isImpersonationMode
    ) {
      return;
    }

    try {
      setIsPreparingHelloSign(true);

      const signatureUrl = await getSignatureUrl(agreement.agreementId, auth);

      // NOTE: "once" method do stack for the same events, we need to clean up the listeners
      // in case user just closes the hellosign modal without signing
      helloSignClient.once("close", () => {
        helloSignClient.off("ready");
        helloSignClient.off("sign");
        helloSignClient.off("error");
      });

      helloSignClient.once("ready", () => setIsPreparingHelloSign(false));

      helloSignClient.once("sign", async () => {
        await callPutUpdateAgreement({
          agreementId: agreement.agreementId,
          status: "Signed",
        });
        statusNotification("Agreement signed successfully", "Success");
        setIsFartherAgreementsModalOpen(false);
      });

      helloSignClient.once("error", () =>
        statusNotification(
          "Failed to sign an agreement. Please try again.",
          "Error"
        )
      );

      helloSignClient.open(signatureUrl);
    } catch {
      statusNotification("Something went wrong. Please try again.", "Error");
      setIsPreparingHelloSign(false);
    }
  };

  const handlePrivacyPolicySignature = (): void => {
    if (privacyPolicy.data) {
      handleSignAgreementViaHelloSign(privacyPolicy.data);
    }
  };

  const handleFartherAgreementSignature = (): void => {
    if (fartherAgreement.data) {
      handleSignAgreementViaHelloSign(fartherAgreement.data);
    }
  };

  const handleIAASignature = (): void => {
    if (investmentAdvisoryAgreement.data) {
      handleSignAgreementViaHelloSign(investmentAdvisoryAgreement.data);
    }
  };

  const determineIncompleteSubtitle = (
    checklistItem: ChecklistItem
  ): string => {
    switch (checklistItem.name) {
      case "Onboarding Agreement":
      case "Privacy Policy":
      case "Investment Advisory Agreement":
        return "Please look over and e-sign";

      case "Disclosures":
        return "Please look over and accept disclosures";

      case "Custodian Agreement":
        return "Waiting for your Advisor's Plan";

      default:
        return "Please fill out";
    }
  };

  const determineOnClickHandler = (
    checklistItem: ChecklistItem
  ): (() => void) => {
    switch (checklistItem.name) {
      case "Investor Profile":
        return () => handleSetDrawerPanelContext("Investor Profile");

      case "Contact Information":
        return () => handleSetDrawerPanelContext("Contact Information");

      case "Additional Information":
        return () => handleSetDrawerPanelContext("Additional Information");

      case "Beneficiaries":
        return () => handleSetDrawerPanelContext("Beneficiaries");

      case "Affiliations":
        return () => handleSetDrawerPanelContext("Affiliations");

      case "Onboarding Agreement":
        return () =>
          checklistItem.value === ClientChecklistStatus.enum.Completed
            ? setIsFartherAgreementsModalOpen(false)
            : setIsFartherAgreementsModalOpen(true);

      case "Privacy Policy":
        return () =>
          checklistItem.value === ClientChecklistStatus.enum.Completed
            ? null
            : handlePrivacyPolicySignature();

      case "Investment Advisory Agreement":
        return () =>
          checklistItem.value === ClientChecklistStatus.enum.Completed
            ? null
            : handleIAASignature();

      case "Disclosures":
        return () =>
          checklistItem.value === ClientChecklistStatus.enum.Completed
            ? setIsDisclosuresModalOpen(false)
            : setIsDisclosuresModalOpen(true);

      case "Custodian Agreement":
        return handleNavigateToDocumentsCenter;

      default:
        return () => null;
    }
  };

  const determineIsStepDisabled = (checklistItem: ChecklistItem): boolean => {
    switch (checklistItem.name) {
      case "Custodian Agreement":
        return (
          checklistItem.value !== ClientChecklistStatus.enum.Completed &&
          !isEveryStepCompletedBeforeCustodianAgreement
        );

      default:
        return false;
    }
  };

  const replaceFartherWhitelabel = (str: string): string => {
    const company = t("companyName") ?? "Farther";

    return str
      .replace("Onboarding", company)
      .replace("Agreement", "Agreements");
  };

  const checklistEntires: ChecklistEntryDetails[] = checklistItems.map(
    (checklistItem) => ({
      title:
        checklistItem.name === "Onboarding Agreement"
          ? replaceFartherWhitelabel(checklistItem.name)
          : checklistItem.name,
      subtitleIncomplete: determineIncompleteSubtitle(checklistItem),
      subtitleComplete: "Complete",
      isStepComplete:
        checklistItem.value === ClientChecklistStatus.enum.Completed,
      isStepDisabled: determineIsStepDisabled(checklistItem),
      onClick: determineOnClickHandler(checklistItem),
    })
  );

  return (
    <>
      {isPreparingHelloSign && <LogoLoadingStill onTop />}

      <FartherAgreementsModal
        isModalOpen={isFartherAgreementsModalOpen}
        isLoading={fartherAgreement.isLoading}
        isErrored={fartherAgreement.hasError}
        onAccept={handleFartherAgreementSignature}
        onClose={() => setIsFartherAgreementsModalOpen(false)}
      />

      {checklistItems.some(
        (checklistItem) => checklistItem.name === "Disclosures"
      ) && (
        <DisclosuresModal
          clientId={clientId}
          isModalOpen={isDisclosuresModalOpen}
          onClose={() => setIsDisclosuresModalOpen(false)}
        />
      )}

      <Checklist checklistEntires={checklistEntires} />
    </>
  );
};

interface ChecklistControllerProps {
  checklist: ClientChecklist | undefined;
}

const ChecklistController: React.FC<ChecklistControllerProps> = ({
  checklist,
  ...restProps
}) => {
  if (!checklist) {
    return null;
  }

  return (
    <HelloSignProvider>
      <ChecklistControllerInternal checklist={checklist} {...restProps} />
    </HelloSignProvider>
  );
};

export default ChecklistController;
